import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useOutlet } from 'react-router-dom';
import { AUTH_SETTLE_MS, AuthStage } from 'storybookConfig/stories/species/Layout/AuthLayout';

interface Page {
  key: string;
  element: ReactNode;
}

/**
 * The auth pages on one stage. The page being left stays mounted for the length of the
 * transition so it can ride the panel out while the next page comes in.
 *
 * That page renders under the new location, so anything in it that navigates from an effect or a
 * <Navigate> fires again when the location changes. Redirect with `replace` (see LogoutPage).
 */
const AuthRoutes = () => {
  const outlet = useOutlet();
  const { pathname } = useLocation();
  const [shown, setShown] = useState<Page>({ key: pathname, element: outlet });
  const [leaving, setLeaving] = useState<Page | null>(null);

  if (shown.key !== pathname) {
    setLeaving(shown);
    setShown({ key: pathname, element: outlet });
  }

  useEffect(() => {
    if (!leaving) return undefined;
    const timeout = setTimeout(() => setLeaving(null), AUTH_SETTLE_MS);
    return () => clearTimeout(timeout);
  }, [leaving]);

  return (
    <AuthStage>
      {leaving && leaving.key !== pathname && (
        <AuthStage.Page key={leaving.key} phase="leave">
          {leaving.element}
        </AuthStage.Page>
      )}
      <AuthStage.Page key={pathname} phase={leaving ? 'enter' : 'idle'}>
        {outlet}
      </AuthStage.Page>
    </AuthStage>
  );
};

export default AuthRoutes;
